import React from 'react';
import { Box, TextField, Typography, Grid, Checkbox, FormControlLabel, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RiskAssessmentItem } from '../types';
import { isSpecialItem, isYesNoItem, isOtherItem, isHourItem, needsRiskRating } from '../utils/riskAssessmentUtils';

interface RiskAssessmentTableProps {
    riskAssessmentItems: RiskAssessmentItem[];
    isViewMode: boolean;
    onRiskAssessmentChange: (index: number, field: keyof RiskAssessmentItem, value: any, subIndex?: number, subField?: string) => void;
    onAddSafetyPrecaution: (index: number) => void;
    onUpdateSafetyPrecautionContent: (index: number, subIndex: number, content: string, type: 'default' | 'extra') => void;
    onDeleteSafetyPrecaution: (index: number, subIndex: number, type: 'default' | 'extra') => void;
}

export const RiskAssessmentTable: React.FC<RiskAssessmentTableProps> = ({
    riskAssessmentItems,
    isViewMode,
    onRiskAssessmentChange,
    onAddSafetyPrecaution,
    onUpdateSafetyPrecautionContent,
    onDeleteSafetyPrecaution
}) => {
    const { t } = useTranslation();

    const renderSpecialItem = (item: RiskAssessmentItem, index: number) => {
        const originalIndex = riskAssessmentItems.findIndex(originalItem => originalItem.itemId === item.itemId);

        return (
            <tr key={item.itemId} style={{ backgroundColor: '#f9f9f9' }}>
                <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                    {item.itemId}
                </td>
                <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }} colSpan={4}>
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.assessmentResult.yes}
                                        onChange={e => {
                                            onRiskAssessmentChange(originalIndex, 'assessmentResult', { yes: e.target.checked, no: false });
                                        }}
                                        disabled={isViewMode}
                                        sx={{ pointerEvents: isViewMode ? 'none' : 'auto' }}
                                    />
                                }
                                label=""
                            />
                            <Typography variant="body2" sx={{ flex: 1, mt: 0.5, fontSize: '0.9rem', lineHeight: 1.4 }}>
                                {item.assessmentContent}
                            </Typography>
                        </Box>

                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="工作方法"
                                    value={item.workMethods}
                                    onChange={e => onRiskAssessmentChange(originalIndex, 'workMethods', e.target.value)}
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                    disabled={isViewMode}
                                    sx={{ mb: 1 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="工業裝置"
                                    value={item.industrialEquipment}
                                    onChange={e => onRiskAssessmentChange(originalIndex, 'industrialEquipment', e.target.value)}
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                    disabled={isViewMode}
                                    sx={{ mb: 1 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="物料"
                                    value={item.materials}
                                    onChange={e => onRiskAssessmentChange(originalIndex, 'materials', e.target.value)}
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                    disabled={isViewMode}
                                    sx={{ mb: 1 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic', fontSize: '0.85rem', color: 'text.secondary' }}>
                                    若工作不涉及工人進入密閉空間內工作,須採取以下措施以確保工人不會進入密閉空間內:
                                </Typography>
                                <TextField
                                    label="非進入措施"
                                    value={item.nonEntryMeasures}
                                    onChange={e => onRiskAssessmentChange(originalIndex, 'nonEntryMeasures', e.target.value)}
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                    disabled={isViewMode}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </td>
                <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                    {/* 特殊項目不顯示安全預防措施 */}
                </td>
            </tr>
        );
    };

    const renderNormalItem = (item: RiskAssessmentItem, index: number) => {
        return (
            <tr key={item.itemId}>
                <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                    {item.itemId}
                </td>
                <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                    <Typography variant="body2">
                        {item.assessmentItems}
                    </Typography>

                    {isYesNoItem(item.itemId) && (
                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.assessmentResult.yes}
                                        onChange={e => {
                                            onRiskAssessmentChange(index, 'assessmentResult', { yes: e.target.checked, no: false });
                                        }}
                                        disabled={isViewMode}
                                        sx={{ pointerEvents: isViewMode ? 'none' : 'auto' }}
                                    />
                                }
                                label="是"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.assessmentResult.no}
                                        onChange={e => {
                                            onRiskAssessmentChange(index, 'assessmentResult', { no: e.target.checked, yes: false });
                                        }}
                                        disabled={isViewMode}
                                        sx={{ pointerEvents: isViewMode ? 'none' : 'auto' }}
                                    />
                                }
                                label="否 (須詳述原因如下:"
                            />
                            {item.assessmentResult.no && (
                                <TextField
                                    value={item.assessmentResult.reason}
                                    onChange={e => onRiskAssessmentChange(index, 'assessmentResult', { reason: e.target.value })}
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={2}
                                    disabled={isViewMode}
                                    sx={{ mt: 1 }}
                                />
                            )}
                        </Box>
                    )}

                    {isHourItem(item.itemId) && (
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                value={item.stayHour || ''}
                                onChange={e => onRiskAssessmentChange(index, 'stayHour', e.target.value)}
                                label={t('Hours')}
                                type="number"
                                size="small"
                                disabled={isViewMode}
                                sx={{ width: 100 }}
                                inputProps={{ min: 0 }}
                            />
                        </Box>
                    )}

                    {isOtherItem(item.itemId) && (
                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.assessmentResult.yes}
                                        onChange={e => {
                                            onRiskAssessmentChange(index, 'assessmentResult', { yes: e.target.checked, no: false });
                                        }}
                                        disabled={isViewMode}
                                        sx={{ pointerEvents: isViewMode ? 'none' : 'auto' }}
                                    />
                                }
                                label="是"
                            />
                            {item.assessmentResult.yes && (
                                <TextField
                                    label="請詳細列出"
                                    value={item.otherAssessmentContent || ''}
                                    onChange={e => onRiskAssessmentChange(index, 'otherAssessmentContent', e.target.value)}
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                    disabled={isViewMode}
                                    sx={{ mt: 1 }}
                                    placeholder="請詳細列出其他評估事項..."
                                />
                            )}
                        </Box>
                    )}
                </td>

                {/* 嚴重性 */}
                <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                    {needsRiskRating(item.itemId) && (
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.severity === 3}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                onRiskAssessmentChange(index, 'severity', 3);
                                            } else {
                                                onRiskAssessmentChange(index, 'severity', 0);
                                            }
                                        }}
                                        disabled={isViewMode}
                                    />
                                }
                                label={t('Very Severe (3)')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.severity === 2}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                onRiskAssessmentChange(index, 'severity', 2);
                                            } else {
                                                onRiskAssessmentChange(index, 'severity', 0);
                                            }
                                        }}
                                        disabled={isViewMode}
                                    />
                                }
                                label={t('Severe (2)')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.severity === 1}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                onRiskAssessmentChange(index, 'severity', 1);
                                            } else {
                                                onRiskAssessmentChange(index, 'severity', 0);
                                            }
                                        }}
                                        disabled={isViewMode}
                                    />
                                }
                                label={t('Minor (1)')}
                            />
                        </Box>
                    )}
                </td>

                {/* 可能性 */}
                <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                    {needsRiskRating(item.itemId) && (
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.likelihood === 3}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                onRiskAssessmentChange(index, 'likelihood', 3);
                                            } else {
                                                onRiskAssessmentChange(index, 'likelihood', 0);
                                            }
                                        }}
                                        disabled={isViewMode}
                                    />
                                }
                                label={t('Very Likely (3)')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.likelihood === 2}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                onRiskAssessmentChange(index, 'likelihood', 2);
                                            } else {
                                                onRiskAssessmentChange(index, 'likelihood', 0);
                                            }
                                        }}
                                        disabled={isViewMode}
                                    />
                                }
                                label={t('Likely (2)')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.likelihood === 1}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                onRiskAssessmentChange(index, 'likelihood', 1);
                                            } else {
                                                onRiskAssessmentChange(index, 'likelihood', 0);
                                            }
                                        }}
                                        disabled={isViewMode}
                                    />
                                }
                                label={t('Unlikely (1)')}
                            />
                        </Box>
                    )}
                </td>

                {/* 風險評級 */}
                <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                    {needsRiskRating(item.itemId) && (
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.riskRating >= 6}
                                        disabled={true}
                                    />
                                }
                                label={t('High Risk (>=6)')}
                                sx={{
                                    backgroundColor: '#ffcdd2',
                                    padding: '2px 4px',
                                    borderRadius: '2px',
                                    margin: '2px 0'
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.riskRating >= 3 && item.riskRating <= 5}
                                        disabled={true}
                                    />
                                }
                                label={t('Medium Risk (3-5)')}
                                sx={{
                                    backgroundColor: '#fff3e0',
                                    padding: '2px 4px',
                                    borderRadius: '2px',
                                    margin: '2px 0'
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={item.riskRating <= 2}
                                        disabled={true}
                                    />
                                }
                                label={t('Low Risk (<=2)')}
                                sx={{
                                    backgroundColor: '#e8f5e8',
                                    padding: '2px 4px',
                                    borderRadius: '2px',
                                    margin: '2px 0'
                                }}
                            />
                        </Box>
                    )}
                </td>

                {/* 安全預防措施 */}
                <td style={{ border: '1px solid #ccc', padding: 8, verticalAlign: 'top' }}>
                    {item.isSafetyPrecautions && (
                        <>
                            {/* 顯示默認安全預防措施 */}
                            {item.defaultSafetyPrecautions?.map((precaution, pIndex) => (
                                <Box key={`default-${pIndex}`} sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={precaution.isChecked}
                                                    onChange={e => {
                                                        onRiskAssessmentChange(index, 'defaultSafetyPrecautions', e.target.checked, pIndex);
                                                    }}
                                                    disabled={isViewMode}
                                                />
                                            }
                                            label=""
                                        />
                                    </Box>
                                    <Typography variant="body2">
                                        {precaution.content}
                                    </Typography>
                                </Box>
                            ))}

                            {/* 顯示額外安全預防措施 */}
                            {item.extraSafetyPrecautions?.map((precaution, pIndex) => (
                                <Box key={`extra-${pIndex}`} sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        {!isViewMode && (
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => onDeleteSafetyPrecaution(index, pIndex, 'extra')}
                                                sx={{ ml: 'auto' }}
                                            >
                                                刪除
                                            </Button>
                                        )}
                                    </Box>
                                    {isViewMode ? (
                                        <Typography variant="body2">
                                            {precaution.content}
                                        </Typography>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            multiline
                                            rows={2}
                                            value={precaution.content}
                                            onChange={e => onUpdateSafetyPrecautionContent(index, pIndex, e.target.value, 'extra')}
                                            placeholder="請填寫安全預防措施內容"
                                        />
                                    )}
                                </Box>
                            ))}

                            {/* 添加按鈕 */}
                            {!isViewMode && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => onAddSafetyPrecaution(index)}
                                    sx={{ mt: 1 }}
                                    fullWidth
                                >
                                    + 添加安全預防措施
                                </Button>
                            )}
                        </>
                    )}
                </td>
            </tr>
        );
    };

    const specialItems = riskAssessmentItems.filter(item => isSpecialItem(item.itemId));
    const normalItems = riskAssessmentItems.filter(item => !isSpecialItem(item.itemId));

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                <thead>
                    <tr style={{ background: '#eee' }}>
                        <th style={{ border: '1px solid #ccc', padding: 8, width: '8%' }}>{t('No.')}</th>
                        <th style={{ border: '1px solid #ccc', padding: 8, width: '20%' }}>{t('Assessment Items')}</th>
                        <th style={{ border: '1px solid #ccc', padding: 8, width: '15%' }}>{t('Severity of Consequence')}</th>
                        <th style={{ border: '1px solid #ccc', padding: 8, width: '15%' }}>{t('Likelihood of Incident')}</th>
                        <th style={{ border: '1px solid #ccc', padding: 8, width: '12%' }}>{t('Risk Rating')}</th>
                        <th style={{ border: '1px solid #ccc', padding: 8, width: '30%' }}>{t('Required Safety Precautions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {/* 特殊項目 */}
                    {specialItems.map((item) => renderSpecialItem(item, riskAssessmentItems.findIndex(i => i.itemId === item.itemId)))}

                    {/* 一般項目 */}
                    {normalItems.map((item, index) => renderNormalItem(item, riskAssessmentItems.findIndex(i => i.itemId === item.itemId)))}
                </tbody>
            </table>
        </Box>
    );
};